import { Link } from 'react-router-dom';
import type { Insight } from '@claude-code-insights/shared';

const CATEGORY_COLORS: Record<string, string> = {
  skill: 'bg-blue-900/40 text-blue-300',
  hook: 'bg-green-900/40 text-green-300',
  'claude-md-pattern': 'bg-purple-900/40 text-purple-300',
  'mcp-server': 'bg-orange-900/40 text-orange-300',
  'prompting-technique': 'bg-pink-900/40 text-pink-300',
  workflow: 'bg-cyan-900/40 text-cyan-300',
  'tool-usage': 'bg-yellow-900/40 text-yellow-300',
  configuration: 'bg-teal-900/40 text-teal-300',
  'general-tip': 'bg-gray-700/40 text-gray-300',
};

const SOURCE_ICONS: Record<string, string> = {
  'x/twitter': '𝕏',
  reddit: '⬡',
  hackernews: 'Y',
  rss: '◎',
  github: '⌥',
  telegram: '✈',
  gmail: '✉',
  bluesky: '☁',
  mastodon: '🐘',
  discord: '💬',
};

interface Props {
  insight: Insight;
}

export function InsightCard({ insight }: Props) {
  const categoryColor =
    CATEGORY_COLORS[insight.category] || CATEGORY_COLORS['general-tip'];
  const sourceIcon = SOURCE_ICONS[insight.sourceType] || '●';

  const relativeDate = formatRelativeDate(insight.createdAt);

  return (
    <Link to={`/insight/${insight.insightId}`} className="card block group">
      <div className="flex items-start gap-3">
        {/* Relevance indicator */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
            insight.relevanceScore >= 8
              ? 'bg-obsidian-success/20 text-obsidian-success'
              : insight.relevanceScore >= 5
                ? 'bg-obsidian-accent/20 text-obsidian-accent'
                : 'bg-obsidian-warning/20 text-obsidian-warning'
          }`}
        >
          {insight.relevanceScore}
        </div>

        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-sm font-medium text-obsidian-text group-hover:text-obsidian-accent transition-colors truncate">
            {insight.title}
          </h3>

          {/* Summary */}
          <p className="mt-1 text-xs text-obsidian-text-muted line-clamp-2">
            {insight.summary}
          </p>

          {/* Meta row */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`badge ${categoryColor}`}>
              {insight.category}
            </span>
            <span className="text-xs text-obsidian-text-faint flex items-center gap-1">
              <span>{sourceIcon}</span>
              <span>{insight.sourceType}</span>
            </span>
            <span className="text-xs text-obsidian-text-faint">
              {relativeDate}
            </span>
          </div>

          {/* Tags */}
          {insight.tags.length > 0 && (
            <div className="mt-2 flex gap-1 flex-wrap">
              {insight.tags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 text-[10px] bg-obsidian-bg-tertiary text-obsidian-text-faint rounded"
                >
                  {tag}
                </span>
              ))}
              {insight.tags.length > 4 && (
                <span className="px-1.5 py-0.5 text-[10px] text-obsidian-text-faint">
                  +{insight.tags.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function formatRelativeDate(iso: string): string {
  const now = Date.now();
  const date = new Date(iso).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
